// Unified analytics wrapper — fires events to GA4 (window.gtag) AND
// Meta Pixel (window.fbq) whenever either is loaded by SiteSettings.jsx.
// Safe no-op when admin hasn't configured tracking IDs.
//
// Usage:
//   import { track, trackPurchase, trackAddToCart } from "@/lib/analytics";
//   track("view_item", { item_id: "abc", item_name: "Tee", value: 599, currency: "INR" });
//
// We deliberately keep this tiny and dependency-free so it never breaks the
// page if a tracker is unavailable.

const CURRENCY = "INR";

// GA4 standard event → Meta Pixel standard event mapping.
// (Meta supports both `track` (standard) and `trackCustom`.)
const FB_STANDARD = {
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  view_cart: "ViewCart",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  sign_up: "CompleteRegistration",
  search: "Search",
  add_to_wishlist: "AddToWishlist",
  generate_lead: "Lead",
  // login + design_save have no Meta standard → trackCustom.
};

function safeGtag(...args) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag(...args);
    }
  } catch (_e) { /* no-op */ }
}

function safeFbq(...args) {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq(...args);
    }
  } catch (_e) { /* no-op */ }
}

/**
 * Fire an event to both GA4 and Meta Pixel.
 * @param {string} name - GA4 event name (e.g. "add_to_cart", "purchase").
 * @param {object} params - event-level params (value, currency, items[], ...).
 */
// Subset of events that we ALSO send server-side so the admin funnel widget
// works without GA4/Meta access. Lightweight POST — never blocks the caller.
const SERVER_FUNNEL_EVENTS = new Set([
  "view_item", "add_to_cart", "view_cart", "begin_checkout",
  "sign_up", "login", "search", "add_to_wishlist", "design_save", "generate_lead",
]);
// `purchase` is intentionally OMITTED — counted server-side from db.orders.

// Stable per-tab session id so server can count unique visitors loosely.
function _sessionId() {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.sessionStorage.getItem("_xyz_sid");
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      window.sessionStorage.setItem("_xyz_sid", sid);
    }
    return sid;
  } catch { return null; }
}

function _detectDevice() {
  if (typeof window === "undefined") return null;
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/ipad|tablet/.test(ua)) return "tablet";
  if (/mobi|android|iphone/.test(ua)) return "mobile";
  return "desktop";
}

function _trafficSource() {
  if (typeof window === "undefined") return "direct";
  try {
    // utm_source survives navigation via sessionStorage so subsequent events
    // are attributed to the original campaign, not "direct".
    const url = new URL(window.location.href);
    const utm = url.searchParams.get("utm_source");
    if (utm) {
      try { window.sessionStorage.setItem("_xyz_utm", utm); } catch {}
      return utm.toLowerCase();
    }
    const cached = window.sessionStorage.getItem("_xyz_utm");
    if (cached) return cached.toLowerCase();
    const ref = document.referrer || "";
    if (!ref) return "direct";
    const refHost = new URL(ref).hostname.toLowerCase();
    if (refHost && refHost !== window.location.hostname) {
      if (/google\./.test(refHost)) return "google";
      if (/facebook|fb\.com|instagram/.test(refHost)) return "facebook";
      if (/youtube/.test(refHost)) return "youtube";
      if (/whatsapp|wa\.me/.test(refHost)) return "whatsapp";
      if (/t\.co|twitter|x\.com/.test(refHost)) return "twitter";
      return refHost.replace(/^www\./, "");
    }
    return "direct";
  } catch { return "direct"; }
}

function _backendBeacon(name, params) {
  if (!SERVER_FUNNEL_EVENTS.has(name)) return;
  try {
    const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/analytics/event";
    const firstItem = (params.items && params.items[0]) || {};
    const body = JSON.stringify({
      event_name: name,
      value: params.value,
      items: params.items,
      session_id: _sessionId(),
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      product_id: firstItem.item_id || firstItem.product_id || null,
      product_slug: firstItem.slug || null,
      product_name: firstItem.item_name || firstItem.name || null,
      traffic_source: _trafficSource(),
      device: _detectDevice(),
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
    });
    // navigator.sendBeacon is the right tool — fire-and-forget, survives page
    // unload (so begin_checkout fires even if the user navigates away mid-render).
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      // Fallback for ancient browsers
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch { /* never break the app for analytics */ }
}

export function track(name, params = {}) {
  if (!name) return;
  // Extract the optional Meta CAPI dedup id — supplied by the caller so the
  // server-side mirror event uses the same id (eventID match → 1 conversion).
  const { _eventID, ...rest } = params;
  // GA4
  safeGtag("event", name, { currency: CURRENCY, ...rest });
  // Meta Pixel — use standard mapping if known, otherwise trackCustom.
  const fbName = FB_STANDARD[name];
  const fbParams = mapToFbParams(name, rest);
  const fbOpts = _eventID ? { eventID: _eventID } : undefined;
  if (fbName) safeFbq("track", fbName, fbParams, fbOpts);
  else safeFbq("trackCustom", name, fbParams, fbOpts);
  // Server-side funnel beacon (best-effort, fire-and-forget)
  _backendBeacon(name, rest);
}

/**
 * Generate a stable, unique event_id for Meta CAPI deduplication.
 * Returns the same id when called within ~5min for the same key (so the
 * client-side Pixel + server-side CAPI fire with the same id).
 */
export function newEventId(prefix = "evt") {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now()}_${rand}`;
}

function mapToFbParams(name, p) {
  const out = { currency: CURRENCY };
  if (p.value != null) out.value = p.value;
  if (p.currency) out.currency = p.currency;
  if (Array.isArray(p.items) && p.items.length) {
    out.content_ids = p.items.map((it) => it.item_id || it.id || it.sku).filter(Boolean);
    out.contents = p.items.map((it) => ({
      id: it.item_id || it.id || it.sku,
      quantity: it.quantity || 1,
      item_price: it.price || it.item_price || undefined,
    }));
    out.content_type = "product";
    out.num_items = p.items.reduce((s, it) => s + (it.quantity || 1), 0);
  } else if (p.item_id || p.id) {
    out.content_ids = [p.item_id || p.id];
    out.content_type = "product";
  }
  if (p.transaction_id) out.order_id = p.transaction_id;
  if (p.search_term) out.search_string = p.search_term;
  return out;
}

// -------- convenience helpers (less typing at call-sites) -------- //

export function trackViewItem(product, opts = {}) {
  if (!product) return;
  track("view_item", {
    value: product.sale_price || product.base_price || 0,
    items: [{
      item_id: product.id || product.slug,
      item_name: product.name,
      price: product.sale_price || product.base_price || 0,
      item_category: product.category || product.main_category,
      quantity: 1,
    }],
    ...opts,
  });
}

export function trackAddToCart(item) {
  if (!item) return;
  track("add_to_cart", {
    value: (item.unit_price || item.price || 0) * (item.quantity || 1),
    items: [{
      item_id: item.product_id || item.id || item.slug,
      item_name: item.name || item.product_name,
      price: item.unit_price || item.price || 0,
      quantity: item.quantity || 1,
    }],
  });
}

export function trackViewCart(cart) {
  if (!cart) return;
  const items = (cart.items || []).map((it) => ({
    item_id: it.product_id || it.id,
    item_name: it.name || it.product_name,
    price: it.unit_price || it.price || 0,
    quantity: it.quantity || 1,
  }));
  track("view_cart", {
    value: cart.total || cart.subtotal || items.reduce((s, it) => s + it.price * it.quantity, 0),
    items,
  });
}

export function trackBeginCheckout(cart) {
  if (!cart) return;
  const items = (cart.items || []).map((it) => ({
    item_id: it.product_id || it.id,
    item_name: it.name || it.product_name,
    price: it.unit_price || it.price || 0,
    quantity: it.quantity || 1,
  }));
  track("begin_checkout", {
    value: cart.total || cart.subtotal || items.reduce((s, it) => s + it.price * it.quantity, 0),
    items,
  });
}

export function trackPurchase(order, eventID) {
  if (!order) return;
  const items = (order.items || []).map((it) => ({
    item_id: it.product_id || it.id,
    item_name: it.name || it.product_name,
    price: it.unit_price || it.price || 0,
    quantity: it.quantity || 1,
  }));
  track("purchase", {
    transaction_id: order.order_number || order.id,
    value: order.total || 0,
    tax: order.tax || 0,
    shipping: order.shipping_fee || order.shipping || 0,
    coupon: order.coupon_code || undefined,
    items,
    _eventID: eventID, // ← dedup with server-side CAPI
  });
}

export function trackSignUp(method = "email") {
  track("sign_up", { method });
}

export function trackLogin(method = "email") {
  track("login", { method });
}

export function trackSearch(query) {
  if (!query) return;
  track("search", { search_term: query });
}

export function trackAddToWishlist(product) {
  if (!product) return;
  track("add_to_wishlist", {
    value: product.sale_price || product.base_price || 0,
    items: [{
      item_id: product.id || product.slug,
      item_name: product.name,
      price: product.sale_price || product.base_price || 0,
    }],
  });
}

export function trackDesignSave(design) {
  // Custom event — no Meta standard equivalent → trackCustom("design_save")
  track("design_save", {
    design_name: design?.name || "untitled",
    product_slug: design?.product_slug || undefined,
  });
}

export function trackGenerateLead(payload = {}) {
  // For bulk-order quote form etc.
  track("generate_lead", { value: payload.value || 0, ...payload });
}
